// Pinned GPT model id constants. These are string-id references used by
// specific call sites that need a particular model. They are validated against
// the live catalog at resolution time via `resolveGptModelId(...)`; if the
// requested id is not in the catalog the resolver falls back to the
// API-supplied default.
export const GPT_5_MODEL_ID = 'gpt-5-2025-08-07';
export const GPT_5_2_MODEL_ID = 'gpt-5.2-2025-12-11';
export const GPT_4_1_MODEL_ID = 'gpt-4.1-2025-04-14';
export const GPT_4_1_NANO_MODEL_ID = 'gpt-4.1-nano-2025-04-14';
export const GPT_4O_2024_08_06_MODEL_ID = 'gpt-4o-2024-08-06';

const EMPTY_CATALOG = { models: [], defaultId: null };

let CATALOG = EMPTY_CATALOG;

export const setGptModelCatalog = (next) => {
  if (!next) {
    CATALOG = EMPTY_CATALOG;
    return;
  }
  CATALOG = {
    models: Array.isArray(next.models) ? next.models : [],
    defaultId: next.defaultId || null,
  };
};

export const getGptModelCatalog = () => CATALOG;

export const getAvailableGptModels = () => CATALOG.models;

export const getDefaultGptModelId = () => CATALOG.defaultId || GPT_5_MODEL_ID;

export const resolveGptModelId = (idOrKey) => {
  if (idOrKey) {
    const byId = CATALOG.models.find((m) => m.id === idOrKey);
    if (byId) return byId.id;
    const byKey = CATALOG.models.find((m) => m.key === idOrKey);
    if (byKey) return byKey.id;
  }
  return getDefaultGptModelId();
};

export const AGENT_NAMES = {
  ANALYST_AT_TORNADO: 'analyst_at_tornado',
  DIGITAL_ANALYST_CHAT: 'chat_analyst',
  TORNADO_CHAT_ANALYST: 'tornado_chat_analyst',
};

export const AI_TOOLS = {
  TABLE_BUILDER_TOOL: 'table_builder_tool',
  LATEST_UPDATES_AND_NEWS: 'latest_updates_and_news',
  EARNINGS_GRID_TEMPLATE: 'earnings_grid_template',
  RECENT_COMPANY_DOCS: 'recent_docs',
  EARNINGS_CALL_ANALYSIS: 'earnings_calls',
  LENDER_EARNINGS_CALL_ANALYSIS: 'lender_earnings_calls',
  COMPANY_SNAPSHOT: 'snapshot',
  CUSTOM_SNAPSHOT: 'custom-snapshot',
  WARNING_INDICATORS: 'warning_indicators',
  BULL_BEAR_CASES: 'bull_bear_cases',
  SCREENING: 'screen',
  RESEARCH_SCREENING: 'research_screen',
  WEBSITE_DOCS: 'website_query',
  QUERY: 'query',
  CHAT_QUERY: 'chat_query',
  RFP_CHAT_QUERY: 'rfp_chat_query',
  AGENT_CHAT_QUERY: 'agent_chat_query',
  RFP_RESPONSE: 'rfp_response_query',
  EVESTMENT_QUERY: 'evestment_query',
  QUERY_RESEARCH_DOCS: 'research_query',
  CREDIT_AGREEMENT_ANALYSIS: 'credit_agreement_analysis',
  LIQUIDITY_TERMS_GRID_MODE: 'liquidity_terms_grid_mode',
  GENERALIZED_GRID_MODE: 'generalized_grid_mode',
  FED_LATEST_DOCS: 'fed_latest_docs',
  FED_QUERY: 'fed_query',
  UPLOAD_SOURCE_DOC: 'upload',
  TEXT_UPLOAD: 'upload',
  WEBPAGE_UPLOAD: 'web_crawl_upload',
  FED_UPLOAD: 'fed_upload',
  EXCEL_UPLOAD: 'excel_upload',
  MS_DOC_UPLOAD: 'ms_doc_upload',
  PPT_UPLOAD: 'ppt_upload',
  TXT_UPLOAD: 'txt_upload',
  DOC_MGMT: 'dm',
  EVAL_TEMPLATE: 'eval_template',
  FED_PUBLIC_RELEASE: 'fed_public_release',
  FED_PUBLIC_SENTIMENT: 'fed_public_sentiment',
  MACRO_ECONOMIC_SIGNALS: 'macro_economic_signals',
  ECONOMIC_IMPACT: 'economic_impact',
  FACTOR: 'factor',
  SPEAKER_TAGGING: 'speaker_tagging',
  COMPANY_TAGGING: 'company_tagging',
  TEXT_SEARCH: 'text_search',
  CITATIONS: 'citations',
  COMPLIANCE: 'compliance',
  FED_GPT: 'fed_custom_client',
  WATCHLIST: 'watchlist',
  GROWTH_EQUITY_ANALYSIS: 'growth_equity_analysis',
  FED_SENTIMENT_V2: 'fed_sentiment_simple_ui',
  DOC_SUMMARY: 'doc_summary',
  QUERY_ENGINE: 'query_flow',
  QUICK_LOOK: 'quick_look',
  QUICK_LOOK_LEGACY: 'quick_look_legacy',

  ESG_UPDATES: 'esg_updates',

  CLIENT_MATRIX_MODE: 'client_matrix_mode',
  CLIENT_PEER_ANALYSIS_MATRIX_MODE: 'client_peer_analysis_matrix_mode',
  CLIENT_QUERY_MODE: 'client_query_mode',
  DOC_TEMPLATE_AGENT_GRID_MODE: 'doc_template_agent_grid_mode',

  CITATIONS_GENERATOR: 'citations_generator',

  QUERY_AGENT_SCRIPT: 'query_agent_script',
  AGENT_SCRIPT_EDITOR: 'agent_script_editor',
  AGENT_FLOW: 'agent_script_flow',
  AGENT_CHAT_QUERY: 'agent_chat_query',

  GROWTH_EQUITY_TEMPLATE: 'growth_equity_memo_template',
  INVESTMENT_MEMO: 'investment_memo_template',
  PORTFOLIO_SNAPSHOT_TEMPLATE: 'portfolio_snapshot_template',
  FINANCIAL_STATEMENT_TEMPLATE: 'financial_statement_template',
  PEER_BENCHMARKING_TEMPLATE: 'peer_benchmarking_template',
  QUARTERLY_DATA_TEMPLATE: 'quarterly_data_template',
  PORTFOLIO_MONITORING_TEMPLATE: 'portfolio_monitoring_template',
  GENERIC_MEMO: 'generic_memo',
  QUICK_LOOK_TEMPLATE: 'quick_look',

  NEW_DEAL_TRIAGE: 'new_deal_triage',
  NEW_DEAL_EVALUATION: 'new_deal_evaluation',

  ADMIN_DOC_REUPLOAD: 'admin_reupload',
  SNOWFLAKE_UPLOAD: 'snowflake_upload',
  GCS_INGESTION: 'gcs_connector',
  BOX_INGESTION: 'box_connector',
  TEMPLATE_REDOWNLOAD: 'template_redownload',

  AGENT_SPREADSHEET: 'agent_spreadsheet',
  REASONING_QUERY: 'reasoning_query',
  QUERY_SERIES: 'query_series',
  DATA_ANALYST_AGENT: 'data_analyst_agent',

  DDQ_TEMPLATE: 'ddq_template',
  DOC_TEMPLATE_PROCESSOR: 'doc_template',
};

export const getAllAITools = () => {
  return Object.values(AI_TOOLS);
};
